import {
  decodeAudio,
  playBuffer,
  readSetting,
  soundOn,
  writeSetting,
} from "./audioEngine";

// De geluidsbestanden in public/sounds/.
//
// Het dashboard maakt zijn geluiden zelf (zie cues.js), en voor tonen werkt
// dat prima. Voor geluid met textuur - papier, knetterend vuurwerk - niet: dat zijn duizenden kleine botsingen, en dat krijg je met
// een paar oscillatoren nooit echt scherp. Daar staan dus opnames voor klaar.
//
// Van het geluid bij een record zijn er twee smaken. Welke je hoort kies je in het
// animatielab, en die keuze staat vast: hij verandert niet mee met de
// geluidsset, want dit is het geluid dat je het vaakst hoort en daar wil je
// niet elke keer over hoeven nadenken.
//
// De bestanden staan in public/ en niet in src/: ze gaan zo ongewijzigd mee
// in de build, en op de server kun je er een vervangen zonder opnieuw te
// bouwen. Ze worden gemaakt door tools/geluiden.py.

const folder = `${process.env.PUBLIC_URL || ""}/sounds`;

// In deze volgorde geprobeerd. mp3 werkt overal, ogg is kleiner bij gelijke
// kwaliteit, wav is onbewerkt en dus groot.
const extensions = ["mp3", "ogg", "wav"];

// Wat er per animatie te kiezen is. De eerste is de standaard, en `gain` is
// om bij te stellen zonder een bestand opnieuw te hoeven exporteren: 1 is
// zoals het bestand zelf is.
//
// Er staat niets voor de confetti: die valt bij een grote deal drie keer
// over elkaar heen, en dat hoort stil te blijven. Zie cues.js.
export const sampleOptions = {
  fireworks: {
    label: "Record",
    hint: "grootste verkoop ooit",
    gain: 0.8,
    files: [
      { file: "record-triomf", name: "Triomf", note: "klap en een akkoord dat blijft staan" },
      { file: "record-sterren", name: "Sterren", note: "alleen fonkelingen, geen klap" },
    ],
  },
  "sale-splash": {
    label: "Aankondiging",
    hint: "alleen bij een set die hier zelf niets voor heeft",
    gain: 0.9,
    files: [{ file: "verkoop", name: "Opname", note: "klap plus akkoord" }],
  },
  "personal-best": {
    label: "Eigen record",
    hint: "iemands grootste verkoop tot nu toe",
    gain: 0.9,
    files: [{ file: "eigen-record", name: "Opname", note: "twee noten omhoog en een akkoord" }],
  },
  "target-hit": {
    label: "Team target",
    hint: "een keer per maand",
    // Dit staat op de tv hard: liever iets onder de rest dan erboven.
    gain: 0.82,
    files: [{ file: "target", name: "Fanfare", note: "koper en een bekkenveeg" }],
  },
  "party-dance": {
    label: "Feest",
    hint: "als iemand het feestje zelf aanzet",
    // Iets onder de rest: dit is het enige geluid dat een kwart minuut
    // doorloopt, en wat je even leuk vindt wordt na tien seconden hard.
    gain: 0.75,
    // Het enige geluid hier dat een heel nummer is en geen aanzet. Zonder
    // fade begint het met een klap en houdt het midden in een maat op; zie
    // playBuffer in audioEngine.js. De uitfade loopt gelijk met het beeld,
    // dat ook eerder tot rust komt dan het nummer afloopt - zie party.js.
    // Rustig op gang: het nummer komt onder het beeld vandaan in plaats van
    // er ineens te zijn. 4,5s, zodat het de eerste maten nodig heeft om op
    // sterkte te komen; de lichtshow komt in hetzelfde tempo op (zie
    // fx-party-lights-fade in animations.css).
    fadeIn: 4.5,
    fadeOut: 3,
    files: [{ file: "feest", name: "Opname", note: "een heel nummer, met fade" }],
  },
  "target-song": {
    label: "Targetfeest",
    hint: "als het team het target haalt",
    gain: 0.75,
    // Hetzelfde nummer als hierboven, maar twee keer zo lang: het target
    // viert langer dan iemand die het feestje zelf aanzet. Het begint als de
    // meter de 100 geraakt heeft en komt in 2,5s op, net voor de klap die 3s
    // in het bestand zit en waar het eerste salvo op afgaat (zie
    // targetPartyGroup in animations/party.js).
    fadeIn: 2.5,
    fadeOut: 3,
    files: [{ file: "feest-target", name: "Opname", note: "31s, met fade" }],
  },
};

// id -> AudioBuffer, of null als er niets ligt. Eén keer per bestand
// uitgezocht: een bestand dat er niet is blijft er niet zijn.
const buffers = new Map();
const loading = new Map();

const storeKey = (id) => `tdSound:${id}`;

// Welk bestand er voor deze animatie gekozen is. Staat er niets, of iets wat
// niet meer bestaat, dan de eerste uit de lijst.
export function chosenFile(id) {
  const option = sampleOptions[id];
  if (!option) return null;

  const saved = readSetting(storeKey(id));
  const known = option.files.some((entry) => entry.file === saved);
  return known ? saved : option.files[0].file;
}

async function fetchBuffer(name) {
  for (const extension of extensions) {
    try {
      const response = await fetch(`${folder}/${name}.${extension}`);
      if (!response.ok) continue;
      const decoded = await decodeAudio(await response.arrayBuffer());
      if (decoded) return decoded;
    } catch {
      // Geen netwerk, of geen bestand. Volgende extensie.
    }
  }
  return null;
}

// Eén bestand klaarzetten. Twee keer hetzelfde vragen levert dezelfde
// belofte op, zodat het niet dubbel over de lijn gaat.
function load(name) {
  if (buffers.has(name)) return Promise.resolve(buffers.get(name));
  if (loading.has(name)) return loading.get(name);

  const promise = fetchBuffer(name).then((buffer) => {
    buffers.set(name, buffer);
    loading.delete(name);
    return buffer;
  });
  loading.set(name, promise);
  return promise;
}

// Alles wat gekozen is ophalen. Dit gebeurt zodra het dashboard staat: een
// bestand dat pas bij de eerste verkoop opgehaald wordt komt te laat, en dan
// hoor je hem die keer nog niet. Alleen het gekozen bestand, niet alle
// smaken - de rest haalt het lab op als je erop drukt.
export function warmSamples() {
  if (!soundOn()) return Promise.resolve();
  return Promise.all(Object.keys(sampleOptions).map((id) => load(chosenFile(id))));
}

// Een andere smaak kiezen. Geeft terug wanneer hij klaar is om te spelen.
export function chooseSample(id, file) {
  const option = sampleOptions[id];
  if (!option || !option.files.some((entry) => entry.file === file)) {
    return Promise.resolve(null);
  }

  writeSetting(storeKey(id), file);
  return load(file);
}

// Het gekozen bestand van deze animatie, als het er is. Geeft terug of er ook
// echt iets is afgespeeld, zodat cues.js weet of hij zelf nog iets moet doen.
// `gain` is die van de set: "Fluister" laat alles zachter klinken.
export function playSample(id, gain = 1) {
  const option = sampleOptions[id];
  if (!option) return false;

  const buffer = buffers.get(chosenFile(id));
  if (!buffer) return false;
  return playBuffer(buffer, {
    gain: option.gain * gain,
    fadeIn: option.fadeIn ?? 0,
    fadeOut: option.fadeOut ?? 0,
  });
}

// Welke bestanden er gevonden zijn. Het animatielab laat dit zien, want "ik
// hoor nog steeds het oude geluid" is bijna altijd een bestand dat net even
// anders heet.
export function foundSamples() {
  return Object.keys(sampleOptions).filter((id) => buffers.get(chosenFile(id)));
}
